import { Alert, Button, Spin } from "antd";
import {
  SwapOutlined,
  CheckOutlined,
  CloseOutlined,
  RobotFilled,
} from "@ant-design/icons";
import Modal from "../../../../../components/generic/Modal";
import { useCallback, useMemo, useState } from "react";
import { StyledRewriteBody } from "./styles";
import Select from "../../../../../components/generic/Select";
import {
  AI_ACTION_CONFIG,
  AI_ACTIONS,
  LANGUAGES,
} from "../../../../../utils/AiService/constant";
import { callAiRequestApi } from "../../../../../utils/AiService/helper";
import { findLast, parseError } from "../../../../../utils/shared";

const RewriteMessage = (props) => {
  const {
    onClose,
    setMessage,
    originalMessage,
    isForMessage = false,
    messages = [],
    selectedAction,
  } = props;

  const [activeAction, setActiveAction] = useState(
    selectedAction || AI_ACTIONS.TRANSLATE,
  );

  const [language, setLanguage] = useState({
    target_language: "Hindi",
    source_language: "English",
  });

  const [result, setResult] = useState({
    [AI_ACTIONS.TRANSLATE]: { isProcessing: false, data: null },
    [AI_ACTIONS.FORMAT_TEXT]: { isProcessing: false, data: null },
    [AI_ACTIONS.SUGGEST_REPLIES]: { isProcessing: false, data: null },
  });

  const { isProcessing, oneProcessing, data } = useMemo(
    () => ({
      isProcessing: result?.[activeAction]?.isProcessing,
      data: result?.[activeAction]?.data || null,
      oneProcessing: Object.keys(result).some((k) => result[k]?.isProcessing),
    }),
    [result, activeAction],
  );

  const onOk = useCallback(async () => {
    try {
      //@ts-ignore
      setResult({
        ...result,
        [activeAction]: { isProcessing: true },
      });
      let payload = {};

      if (activeAction === AI_ACTIONS.FORMAT_TEXT) {
        payload.text = originalMessage;
      } else if (activeAction === AI_ACTIONS.TRANSLATE) {
        payload = {
          message: originalMessage,
          ...language,
        };
      } else if (activeAction === AI_ACTIONS.SUGGEST_REPLIES) {
        const historyMessages =
          (messages || [])?.slice(-5)?.filter((msg) => msg?.content) || [];

        const lastMessage = findLast(messages || [], "sender_type", "customer");

        if (!lastMessage?.content)
          throw Error("At least one previous message required..");

        payload = {
          incoming_message: lastMessage?.content || "",
          conversation_history: historyMessages.map((m) => ({
            role: m?.sender_type !== "customer" ? "advisor" : m?.sender_type,
            text: m?.content,
          })),
        };
      }
      const res = await callAiRequestApi({
        action: activeAction,
        payload,
      });

      const data =
        AI_ACTIONS.SUGGEST_REPLIES === activeAction
          ? res?.suggestions?.map(({ reply_text }) => reply_text)
          : res?.formatted || res?.translated_message;

      setResult({
        ...result,
        [activeAction]: {
          isProcessing: false,
          data,
        },
      });
    } catch (error) {
      parseError(error, true);
      //@ts-ignore
      setResult({
        ...result,
        [activeAction]: { isProcessing: false },
      });
    }
  }, [result, language, isProcessing, activeAction, originalMessage, messages]);

  return (
    <Modal
      title={"Rewrite Message"}
      open
      onCancel={onClose}
      isProcessing={oneProcessing}
      footer={null}
      width={500}
    >
      <StyledRewriteBody>
        {!isForMessage && (
          <div className="action-tabs">
            {Object.keys(AI_ACTION_CONFIG).map((key) => (
              <Button
                key={key}
                className={`action-tab ${activeAction === key ? "active" : ""}`}
                onClick={() => setActiveAction(key)}
                disabled={isProcessing}
              >
                <span className="tab-icon">{AI_ACTION_CONFIG[key].icon}</span>
                {AI_ACTION_CONFIG[key].label}
              </Button>
            ))}
          </div>
        )}
        {activeAction === AI_ACTIONS.SUGGEST_REPLIES ? (
          <Alert
            showIcon
            description="These suggestions are generated using the customer's most recent message."
          />
        ) : (
          <div className="original-div">
            <div className="rewrite-label">Original Message</div>
            <div className="original-box">{originalMessage}</div>
          </div>
        )}
        <div>
          {activeAction === AI_ACTIONS.TRANSLATE && (
            <>
              <div className="lang-row">
                <div className="lang-select">
                  <div className="rewrite-label">Source</div>
                  <Select
                    className="w-100"
                    value={language.source_language}
                    onChange={(value) =>
                      setLanguage({ ...language, source_language: value })
                    }
                    options={LANGUAGES}
                  />
                </div>

                <Button
                  onClick={() =>
                    setLanguage({
                      target_language: language.source_language,
                      source_language: language.target_language,
                    })
                  }
                  style={{ marginTop: 20 }}
                  icon={<SwapOutlined />}
                />

                <div className="lang-select">
                  <div className="rewrite-label">Target</div>
                  <Select
                    className="w-100"
                    value={language.target_language}
                    onChange={(value) =>
                      setLanguage({ ...language, target_language: value })
                    }
                    options={LANGUAGES}
                  />
                </div>
              </div>
            </>
          )}

          <div className="result-box">
            {isProcessing && (
              <div className="flex w-100 justify-center">
                <Spin />
              </div>
            )}
            {!data && !isProcessing && (
              <Button
                onClick={onOk}
                icon={<RobotFilled />}
                variant="outlined"
                className="w-100"
              >
                {AI_ACTION_CONFIG[activeAction].buttonLabel}
              </Button>
            )}

            {!isProcessing && data && (
              <>
                <div className="rewrite-label"> Result</div>

                {activeAction === "suggest_replies" ? (
                  <div className="suggestions-list">
                    {data?.map((suggestion, index) => (
                      <div className="suggestion-item" key={index}>
                        <span className="suggestion-text">{suggestion}</span>
                        <Button
                          color="primary"
                          variant="outlined"
                          onClick={() => setMessage(suggestion)}
                        >
                          Use this
                        </Button>
                      </div>
                    ))}
                  </div>
                ) : (
                  <>
                    <div className="result-text">{data}</div>
                    <div className="accept-ignore-row">
                      <Button
                        icon={<CheckOutlined />}
                        color="green"
                        variant="outlined"
                        className="w-100"
                        onClick={() => setMessage(data)}
                      >
                        Accept
                      </Button>
                      <Button
                        danger
                        icon={<CloseOutlined />}
                        onClick={onClose}
                        className="w-100"
                      >
                        Ignore
                      </Button>
                    </div>
                  </>
                )}
              </>
            )}
          </div>
        </div>
      </StyledRewriteBody>
    </Modal>
  );
};

export default RewriteMessage;
